<template>
    <div class="flex items-center justify-between">
        <div>
            <div v-if="selectionEnabled && selectedItemsCount > 0" class="text-semi-bold">
                {{
                    trans('general.selected_items.label', {
                        count: selectedItemsCount,
                    })
                }}
            </div>
        </div>
        <div v-if="showPagination" :style="{ 'min-height': !paginationContainer ? '44px' : null }">
            <teleport :disabled="!paginationContainer" :to="paginationContainer" defer>
                <pagination :paginator="items" :query-params="queryParams" />
            </teleport>
        </div>
    </div>
    <sticky-table-header>
        <div class="table-responsive relative">
            <table
                :class="{
                    'table-fixed': items?.data.length === 0,
                    'table-empty': items?.data.length === 0,
                }"
                class="table-rounded table-striped table-hover table border"
            >
                <thead>
                    <tr>
                        <th v-if="!!selectionEnabled" style="vertical-align: middle; width: 40px !important; min-width: 40px !important; padding: 0">
                            <div
                                v-if="items?.data.length"
                                class="table-all-selector flex items-start justify-center"
                                style="height: 100%; cursor: pointer"
                                @click.prevent="toggleAllSelection"
                            >
                                <input
                                    :checked="areAllItemsSelected"
                                    :indeterminate="isIndeterminate"
                                    class="form-check-input m-0"
                                    readonly
                                    type="checkbox"
                                />
                            </div>
                        </th>
                        <th
                            v-for="(header, idx) in columns"
                            :style="[
                                header._style,
                                {
                                    cursor: isColumnSortable(header) ? 'pointer' : 'default',
                                },
                            ]"
                            class="position-relative text-nowrap"
                            @click="updateSorting(header)"
                        >
                            <div class="flex items-center justify-between" style="gap: 5px">
                                <slot
                                    v-if="slots['header-' + header.key]"
                                    :key="idx"
                                    :header="header"
                                    :name="`header-${header.key}`"
                                    :style="header._style"
                                />
                                <span v-else>{{ header.label }}</span>
                                <sort-icon
                                    v-if="isColumnSortable(header)"
                                    :state="getSortableColumnState(header)"
                                    style="position: relative; top: 2px"
                                />
                            </div>
                        </th>
                        <th v-if="actions && actions.length && items?.data.length" style="width: 0px" />
                    </tr>
                </thead>
                <tbody>
                    <template v-if="!items">
                        <tr v-for="n in 10" :key="n" style="cursor: default">
                            <td v-if="selectionEnabled" />
                            <template v-for="(column, idx) in columns">
                                <td class="align-middle">
                                    <skeleton-loader />
                                </td>
                            </template>
                        </tr>
                    </template>
                    <template v-if="items && items.data.length === 0">
                        <tr>
                            <td :colspan="columnsLength">
                                <div class="my-5 text-center">
                                    <h2 class="flex items-center justify-center" style="gap: 5px">
                                        {{ noItemsView.noItems }}
                                        <icon class="text-danger" icon="cil-ban" style="width: 30px; height: 30px" />
                                    </h2>
                                </div>
                            </td>
                        </tr>
                    </template>
                    <template v-else>
                        <tr
                            v-for="(row, index) in rows"
                            :key="rowKey(row, index)"
                            :data-id="row.id"
                            :style="{
                                cursor: rowClickable ? 'pointer' : 'default',
                            }"
                            @click="onRowClick(row)"
                        >
                            <td v-if="selectionEnabled" class="table-row-selector text-center align-middle" @click.stop="toggleItemSelection(row.id)">
                                <jet-checkbox :model-value="isSelectedItem(row.id)" />
                            </td>
                            <template v-for="(column, idx) in columns">
                                <slot v-if="slots[column.key]" :key="idx" :item="row" :name="column.key" :style="column._style" />
                                <td v-else class="align-middle">
                                    {{ row[column.key] }}
                                </td>
                            </template>
                            <td v-if="hasActions" :class="actionClasses">
                                <div class="flex items-center justify-end" style="gap: 5px" @click.stop @dblclick.stop>
                                    <template v-for="(action, idx) in actions" :key="action.id || idx">
                                        <jet-button
                                            v-if="shouldRenderAction(action, row)"
                                            :class="{ 'btn-icon': action.icon }"
                                            :destructive="action.type === 'danger'"
                                            :disabled="isActionProcessing(action, row, idx) || isActionInFlight(action, row, idx)"
                                            :is-processing="isActionProcessing(action, row, idx)"
                                            type="button"
                                            variant="secondary"
                                            @click.stop.prevent="handleActionClick(action, row, idx)"
                                            @dblclick.stop.prevent="handleActionClick(action, row, idx)"
                                        >
                                            <icon v-if="action.icon" v-c-tooltip="action.tooltip || ''" :icon="action.icon" />
                                            {{ action.label }}
                                        </jet-button>
                                    </template>
                                </div>
                            </td>
                        </tr>
                    </template>
                </tbody>
            </table>
        </div>
    </sticky-table-header>
</template>
<script setup>
import StickyTableHeader from '@/Components/StickyTableHeader.vue';
import { computed, ref, useSlots } from 'vue';
import SortIcon from '@/Pages/Assets/SortIcon.vue';

import JetButton from '@/Jetstream/Button.vue';
import JetCheckbox from '@/Jetstream/Checkbox.vue';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';
import useSelectedItems from '@/Utils/UseSelectedItems';
import SkeletonLoader from '@/Components/SkeletonLoader.vue';
import Pagination from '@/Components/Pagination.vue';

const props = defineProps({
    class: String,
    columns: Array,
    items: Object,
    queryParams: Object,
    noItemsView: Object,
    paginationContainer: String,
    showPagination: {
        type: Boolean,
        default: true,
    },
    actions: {
        type: Array,
        default: () => [],
    },
    rowClickable: {
        type: Boolean,
        default: true,
    },
    actionClasses: {
        type: [String, Object, Array],
        default: 'align-middle',
    },
    selectionEnabled: [String, Boolean],
    processingItems: Array,
    stickyHeader: {
        type: Object,
    },
});
const emit = defineEmits(['row-clicked']);
const slots = useSlots();

const rows = computed(() => props.items?.data ?? []);
const hasActions = computed(() => Array.isArray(props.actions) && props.actions.length > 0);
const rowKey = (row, index) => row?.id ?? index;

const meta = computed(() => {
    return props.items?.meta || {};
});

const { selectedItemsCount, isSelectedItem, isIndeterminate, areAllItemsSelected, toggleAllSelection, toggleItemSelection } = useSelectedItems(
    props.selectionEnabled,
    meta,
);

const updateSorting = (column) => {
    // Column sorting needs to be switched off explicitly
    if (column.sorter === false) {
        return;
    }

    if (!props.queryParams?.form?.sort || typeof props.queryParams.change !== 'function') {
        return;
    }
    const previousColumn = props.queryParams.form.sort.column;
    const previousAsc = props.queryParams.form.sort.asc;
    const isSameColumn = previousColumn === column.key;
    props.queryParams.form.sort.column = column.key;
    props.queryParams.form.sort.asc = isSameColumn ? !previousAsc : true;
    props.queryParams.change();
};

const columnsLength = computed(() => {
    let count = props.columns.length;
    if (hasActions.value && rows.value.length) {
        count += 1;
    }
    if (props.selectionEnabled) {
        count += 1;
    }

    return count;
});

const isColumnSortable = (column) => props.items?.data.length && (column.sorter === undefined || !!column.sorter);

const getSortableColumnState = (column) => {
    if (isColumnSortable(column)) {
        if (props.queryParams?.form.sort.column === column.key) {
            return props.queryParams.form.sort.asc ? 'asc' : 'desc';
        }
    }
    return 'none';
};

const onRowClick = (row) => {
    if (!props.rowClickable) return;
    emit('row-clicked', row);
};

// Prevent duplicate rapid calls and accidental row clicks from action buttons
const inFlight = ref(new Set());
const actionKey = (action, row, idx) => `${action.id || idx}-${row.id}`;
const isActionProcessing = (action, row, idx) => props.processingItems?.includes(actionKey(action, row, idx));
const isActionInFlight = (action, row, idx) => inFlight.value.has(actionKey(action, row, idx));
const handleActionClick = (action, row, idx) => {
    if (!shouldRenderAction(action, row)) return;
    const key = actionKey(action, row, idx);
    if (inFlight.value.has(key) || isActionProcessing(action, row, idx)) return;
    inFlight.value.add(key);
    // Recreate Set to ensure reactivity
    inFlight.value = new Set(inFlight.value);
    try {
        action.click(row);
    } finally {
        // Debounce window to block double-clicks; processingItems will also disable while async is running
        setTimeout(() => {
            if (inFlight.value.has(key)) {
                inFlight.value.delete(key);
                inFlight.value = new Set(inFlight.value);
            }
        }, 600);
    }
};

function shouldRenderAction(action, item) {
    let shouldRender = true;

    if (action.permissions && !hasAnyPermission(action.permissions)) {
        shouldRender = false;
    }

    if (action.condition && item && !action.condition(item)) {
        shouldRender = false;
    }

    return shouldRender;
}
</script>
